import { defineStore } from 'pinia'
import { ref } from 'vue'
import { IBASE_URL } from '@/config/env'
import router from '@/router'

export interface UserInfo {
  id: string
  email: string
  name: string
  role: string
  tenant_id?: string
}

export const useAuthStore = defineStore('auth', () => {
  // Backward compat: read existing localStorage token during transition
  const token = ref<string>(localStorage.getItem('token') || '')
  const user = ref<UserInfo | null>(null)

  const setToken = (newToken: string) => {
    token.value = newToken
    // No longer persist to localStorage — HttpOnly cookie handles session
  }

  const clearToken = () => {
    token.value = ''
    user.value = null
    localStorage.removeItem('token')
  }

  const isLoggedIn = () => !!token.value || !!user.value

  /** Check cookie session by calling iBase /api/auth/me */
  async function init() {
    // Public routes (e.g. marketing/demo pages) should not trigger auth requests
    if (router.currentRoute.value.meta.public) {
      return false
    }
    try {
      const res = await fetch(`${IBASE_URL}/api/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          ...(token.value ? { Authorization: `Bearer ${token.value}` } : {}),
        },
      })
      if (res.ok) {
        const data = await res.json()
        user.value = {
          id: data.id,
          email: data.email,
          name: data.name || data.email,
          role: data.role,
          tenant_id: data.tenant_id,
        }
        // If backend returns a fresh token, keep it in memory
        if (data.token) {
          token.value = data.token
        }
        return true
      }
    } catch {
      // silent fail — user not authenticated via cookie
    }
    // If cookie auth failed but we have localStorage token, keep it for backward compat
    if (!token.value) {
      user.value = null
    }
    return false
  }

  /** Call backend logout to clear HttpOnly cookie, then clear local state */
  async function logout() {
    try {
      await fetch(`${IBASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...(token.value ? { Authorization: `Bearer ${token.value}` } : {}),
        },
      })
    } catch {
      // ignore network error
    }
    clearToken()
  }

  return {
    token,
    user,
    setToken,
    clearToken,
    isLoggedIn,
    init,
    logout,
  }
})
